import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n/LanguageContext';
import {
  HELP_ABOUT,
  HELP_ARTICLES,
  HELP_CONTACT_KEYS,
  HELP_INDEX_INTRO_KEY,
  HELP_PROSE
} from '../content/helpArticles.generated';
import type { HelpArticle, HelpArticleId, HelpBlock } from '../content/helpTypes';

const SUPPORT_EMAIL = 'support@chesswiz.net';

const ARTICLE_ICONS: Record<HelpArticleId, React.ElementType> = {
  'analysis-alarms': NotificationsActiveOutlinedIcon,
  'opening-notifications': CampaignOutlinedIcon,
  practices: SchoolOutlinedIcon,
  'game-review': InsightsOutlinedIcon,
  analytics: QueryStatsOutlinedIcon,
  'openings-analytics': TrendingUpOutlinedIcon,
  subscription: WorkspacePremiumOutlinedIcon
};

const PILLAR_ICONS: Record<string, React.ElementType> = {
  alarms: NotificationsActiveOutlinedIcon,
  practice: SchoolOutlinedIcon,
  reviews: InsightsOutlinedIcon,
  daily: CalendarMonthOutlinedIcon
};

/** Renders one prose block. CTA blocks are intentionally dropped on the public
 *  page — a logged-out reader cannot open in-app features. */
const HelpBlockView: React.FC<{ block: HelpBlock; text: (key: string) => string }> = ({
  block,
  text
}) => {
  switch (block.type) {
    case 'heading': {
      const value = text(block.key);
      if (!value) return null;
      return (
        <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
          {value}
        </Typography>
      );
    }
    case 'paragraph': {
      const value = text(block.key);
      if (!value) return null;
      return (
        <Typography variant="body1" sx={{ mb: 1.5, color: 'text.secondary', lineHeight: 1.7 }}>
          {value}
        </Typography>
      );
    }
    case 'bullets': {
      const items = block.keys.map(text).filter(Boolean);
      if (items.length === 0) return null;
      return (
        <List sx={{ listStyleType: 'disc', pl: 3, py: 0, mb: 1.5 }}>
          {items.map((item, index) => (
            <ListItem
              key={index}
              sx={{ display: 'list-item', px: 0, py: 0.4, color: 'text.secondary', lineHeight: 1.6 }}
            >
              {item}
            </ListItem>
          ))}
        </List>
      );
    }
    case 'tip': {
      const value = text(block.key);
      if (!value) return null;
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            bgcolor: 'rgba(46, 125, 50, 0.08)',
            borderRadius: 1,
            px: 1.75,
            py: 1.5,
            mb: 2
          }}
        >
          <LightbulbOutlinedIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
          <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
            {value}
          </Typography>
        </Box>
      );
    }
    case 'glossary': {
      const entries = block.entries
        .map(entry => ({ term: text(entry.termKey), definition: text(entry.definitionKey) }))
        .filter(entry => entry.term && entry.definition);
      if (entries.length === 0) return null;
      return (
        <Card
          variant="outlined"
          sx={{ borderRadius: 2, mb: 2, borderColor: 'rgba(34, 44, 55, 0.12)' }}
        >
          {entries.map((entry, index) => (
            <Box
              key={entry.term}
              sx={{
                px: 2,
                py: 1.5,
                borderTop: index > 0 ? '1px solid rgba(34, 44, 55, 0.08)' : 'none'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {entry.term}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {entry.definition}
              </Typography>
            </Box>
          ))}
        </Card>
      );
    }
    case 'cta':
    default:
      return null;
  }
};

const ArticleSection: React.FC<{ article: HelpArticle; text: (key: string) => string }> = ({
  article,
  text
}) => {
  const Icon = ARTICLE_ICONS[article.id];
  const title = text(article.titleKey);
  return (
    <Card
      id={article.id}
      elevation={0}
      sx={{
        scrollMarginTop: 88,
        borderRadius: 4,
        border: '1px solid rgba(34, 44, 55, 0.08)',
        boxShadow: '0 18px 60px rgba(15, 23, 42, 0.06)',
        p: { xs: 2.5, md: 4 },
        mb: 3
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(46, 125, 50, 0.12)',
            color: 'primary.main'
          }}
        >
          {Icon ? <Icon /> : null}
        </Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Stack>
      <Box sx={{ mt: 1 }}>
        {article.blocks.map((block, index) => (
          <HelpBlockView key={index} block={block} text={text} />
        ))}
      </Box>
    </Card>
  );
};

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();
  const { locale, t } = useI18n();

  const prose = HELP_PROSE[locale] ?? HELP_PROSE.en;
  const text = React.useCallback(
    (key: string) => prose[key] ?? HELP_PROSE.en[key] ?? '',
    [prose]
  );

  const jumpTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const contactSubject = encodeURIComponent(text('help.contact.subject') || 'ChessWiz support');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(46, 125, 50, 0.14), transparent 28%), linear-gradient(180deg, #F7F8F5 0%, #FFFFFF 38%, #F4F7FB 100%)'
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#17351F' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            aria-label={t.howItWorks.backHome}
            sx={{ mr: 1.5 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t.howItWorks.title}
          </Typography>
          <LanguageSelector variant="dark" />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Hero */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 900, letterSpacing: '-0.02em', mb: 1.5 }}
          >
            {t.howItWorks.title}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.6 }}>
            {text(HELP_INDEX_INTRO_KEY)}
          </Typography>
        </Box>

        {/* What ChessWiz does — the pitch a newcomer reads first */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(34, 44, 55, 0.08)',
            bgcolor: 'rgba(46, 125, 50, 0.06)',
            p: { xs: 3, md: 4 },
            mb: 5
          }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
            {text(HELP_ABOUT.titleKey)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 3 }}>
            {text(HELP_ABOUT.bodyKey)}
          </Typography>
          <Grid container spacing={2}>
            {HELP_ABOUT.pillars.map(pillar => {
              const Icon = PILLAR_ICONS[pillar.id];
              return (
                <Grid item xs={12} sm={6} key={pillar.id}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: '#FFFFFF',
                        color: 'primary.main',
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)'
                      }}
                    >
                      {Icon ? <Icon fontSize="small" /> : null}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {text(pillar.titleKey)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>
                        {text(pillar.descKey)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Card>

        {/* Jump-to cards */}
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {HELP_ARTICLES.map(article => {
            const Icon = ARTICLE_ICONS[article.id];
            return (
              <Grid item xs={12} sm={6} key={article.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(34, 44, 55, 0.08)',
                    transition: 'box-shadow 120ms ease, transform 120ms ease',
                    '&:hover': { boxShadow: '0 16px 40px rgba(15, 23, 42, 0.10)', transform: 'translateY(-2px)' }
                  }}
                >
                  <CardActionArea
                    onClick={() => jumpTo(article.id)}
                    sx={{ height: '100%', p: 2, alignItems: 'flex-start' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(46, 125, 50, 0.12)',
                          color: 'primary.main'
                        }}
                      >
                        {Icon ? <Icon fontSize="small" /> : null}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {text(article.titleKey)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          {text(article.teaserKey)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Articles */}
        {HELP_ARTICLES.map(article => (
          <ArticleSection key={article.id} article={article} text={text} />
        ))}

        {/* Contact */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(34, 44, 55, 0.08)',
            bgcolor: 'rgba(46, 125, 50, 0.06)',
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            mt: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            {text(HELP_CONTACT_KEYS.title)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
            {text(HELP_CONTACT_KEYS.subtitle)}
          </Typography>
          <Button
            variant="contained"
            size="large"
            href={`mailto:${SUPPORT_EMAIL}?subject=${contactSubject}`}
            sx={{ borderRadius: 999, px: 4, textTransform: 'none', fontWeight: 700 }}
          >
            {text(HELP_CONTACT_KEYS.cta)}
          </Button>
        </Card>

        {/* Footer */}
        <Divider sx={{ my: 5 }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            {t.footer.rights}
          </Typography>
          <Stack direction="row" spacing={2.5}>
            <Link component={RouterLink} to="/" underline="hover" color="text.secondary" variant="body2">
              {t.howItWorks.backHome}
            </Link>
            <Link component={RouterLink} to="/privacy" underline="hover" color="text.secondary" variant="body2">
              {t.footer.privacy}
            </Link>
            <Link component={RouterLink} to="/support" underline="hover" color="text.secondary" variant="body2">
              {t.footer.support}
            </Link>
            <Link component={RouterLink} to="/terms" underline="hover" color="text.secondary" variant="body2">
              {t.footer.terms}
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default HowItWorksPage;
